import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useResponsive } from '../../../hooks';
import { useStore } from '../../../store/state';
import { Breakpoint } from '../../styling/breakpoints';
import { defaultTaxInfo } from '../../pages/Register/data';
import {
  RequestGetInfluencerTaxInformation,
  RequestPutInfluencerTaxInformation,
  RequestPutUser,
} from '../../../store/User/actions';
import { taxInfoReducer } from '../../pages/Profile/TaxInformation/reducer';
import { transformTaxInfo } from '../../pages/Profile/TaxInformation/useTaxInformation';
import { detailsReducer } from '../../pages/Profile/reducers';
import { defaultDetails } from '../../pages/Profile/data';
import { InterestOption } from '../../common/Interests/types';
import { isPostalRequired } from '../../../utils/localisation_countries.utils';
import { validateVAT } from '../../../utils/vatValidation.utils';

export const useVerifyInformation = (acceptTerms: () => void) => {
  const [{ userState }, dispatch] = useStore();
  const user = userState.user;
  const storeTaxInformation = userState.taxInformation;

  const { layout } = useResponsive();
  const isMobile = layout <= Breakpoint.MOBILE;

  const [taxInformation, taxDispatch] = useReducer(
    taxInfoReducer,
    defaultTaxInfo
  );

  const influencerId = userState.user?.influencer_id;

  useEffect(() => {
    if (influencerId)
      RequestGetInfluencerTaxInformation(dispatch, influencerId);
  }, [dispatch, influencerId]);

  useEffect(() => {
    if (storeTaxInformation) {
      taxDispatch({
        type: 'INITIALISE',
        payload: storeTaxInformation,
      });
    }
  }, [storeTaxInformation]);

  const currentInterests = useMemo((): InterestOption[] => {
    return defaultDetails.interests.map((interest: InterestOption) => {
      if (user && user.influencer_interests.includes(interest.value)) {
        return { ...interest, active: true };
      } else {
        return { ...interest, active: false };
      }
    });
  }, [user]);

  const initialDetails = userState.user?.influencer_address
    ? {
        firstName: userState.user?.influencer_first_name,
        lastName: userState.user?.influencer_last_name,
        address1: userState.user?.influencer_address.line_one,
        address2: userState.user?.influencer_address.line_two,
        city: userState.user?.influencer_address.city,
        postCode: userState.user?.influencer_address.postcode,
        phone: userState.user?.influencer_phone_number,
        country: userState.user?.influencer_address.country,
        interests: currentInterests,
      }
    : defaultDetails;

  const [details, detailsDispatch] = useReducer(detailsReducer, initialDetails);

  const handleDetailsChange = useCallback((type: string, payload: string) => {
    detailsDispatch({ type, payload });
  }, []);

  const onSubmit = () => {
    if (influencerId && !!userState.user?.influencer_address) {
      Promise.all([
        RequestPutInfluencerTaxInformation(
          dispatch,
          influencerId,
          transformTaxInfo(
            taxInformation,
            storeTaxInformation,
            userState.user.influencer_address
          )
        ),
        RequestPutUser(
          dispatch,
          {
            influencer_id: userState.user.influencer_id,
            keycloak_id: userState.user.keycloak_id,
            influencer_first_name: userState.user.influencer_first_name,
            influencer_last_name: userState.user.influencer_last_name,
            influencer_address: {
              line_one: details.address1,
              line_two: details.address2,
              city: details.city,
              postcode: isPostalRequired(details.country)
                ? details.postCode
                : '000 000',
              country: details.country,
            },
            influencer_phone_number: userState.user.influencer_phone_number,
            influencer_interests: userState.user.influencer_interests,
            influencer_tours_viewed: userState.user.influencer_tours_viewed,
            influencer_language: userState.user.influencer_language,
          },
          false
        ),
      ]).then(userState.errors.taxInformation ? undefined : acceptTerms);
    }
  };

  const [selfBillAccepted, setSelfBillAccepted] = useState(false);
  const toggleSelfbill = () => setSelfBillAccepted(!selfBillAccepted);

  const isVATValid =
    (taxInformation.isVATRegistered === 'yes' &&
      validateVAT(details.country, taxInformation.vatNumber) &&
      taxInformation.vatNumber != '') ||
    taxInformation.isVATRegistered === 'no';

  const [validVAT, setValidVAT] = useState(isVATValid);

  useEffect(() => {
    setValidVAT(validateVAT(details.country, taxInformation.vatNumber));
  }, [details.country, taxInformation.vatNumber]);

  const isLLCValid =
    (taxInformation.isLLC === 'yes' &&
      taxInformation.companyName !== '' &&
      taxInformation.companyNumber !== '') ||
    taxInformation.isLLC === 'no';

  const isAddressValid =
    details.address1.trim().length > 0 &&
    details.city.trim().length > 0 &&
    (!isPostalRequired(details.country) || details.postCode.trim().length > 0);

  return {
    taxInformation,
    taxDispatch,
    isMobile,
    details,
    detailsDispatch,
    onSubmit,
    handleDetailsChange,
    selfBillAccepted,
    toggleSelfbill,
    disableSubmit: !isLLCValid || !isVATValid || !isAddressValid,
    validVAT,
    error: userState.errors.taxInformation ?? userState.fetchError?.message,
    isLoading: userState.isLoading.taxInformation || userState.isFetching,
  };
};
